/** `/catalog/sponsors`, `/catalog/sponsors/:id`: sponsor brands (§7) — logos light/dark, colour, tagline, links, CTA, tier, weight. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SponsorTier, type SponsorAdminDto, type UpsertSponsorRequest } from '@windrop/contracts';
import { Button, Field, Input, Select, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { isApiError } from '../../lib/api/client';
import { catalogApi } from './api';
import { AssetUploader } from './AssetUploader';

export function SponsorsListPage() {
  const q = useQuery({ queryKey: ['sponsors'], queryFn: () => catalogApi.sponsors.list() });
  const columns = useMemo<Column<SponsorAdminDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Sponsor',
        sortValue: (r) => r.name.toLowerCase(),
        cell: (r) => (
          <Link to={`/catalog/sponsors/${r.sponsorId}`}>
            <strong>{r.name}</strong>
          </Link>
        ),
      },
      { id: 'tier', header: 'Tier', sortValue: (r) => r.tier, cell: (r) => r.tier },
      { id: 'weight', header: 'Weight', align: 'end', cell: (r) => r.displayWeight },
      {
        id: 'contrast',
        header: 'Brand colour',
        cell: (r) =>
          r.brandColor
            ? `${r.brandColor} · ${r.contrastOk ? 'contrast ok' : 'fails 3:1 on light canvas'}`
            : '—',
      },
      { id: 'viewers', header: 'Sponsor logins', align: 'end', cell: (r) => r.viewerUserIds.length },
    ],
    [],
  );
  return (
    <>
      <PageHeader
        title="Sponsors"
        breadcrumb={[{ label: 'Sponsors & prizes', to: '/catalog' }, { label: 'Sponsors' }]}
        actions={
          <Can permission="sponsors.manage">
            <Button href="/catalog/sponsors/new">New sponsor</Button>
          </Can>
        }
      />
      <DataTable
        caption="Sponsors"
        columns={columns}
        rows={q.data ?? []}
        rowKey={(r) => r.sponsorId}
        loading={q.isPending}
        emptyState={
          <EmptyState
            title="No sponsors"
            body="Sponsors stay visible from landing to claim without touching outcomes (§7)."
            action={<Button href="/catalog/sponsors/new">New sponsor</Button>}
          />
        }
      />
    </>
  );
}

const blank = (): UpsertSponsorRequest => ({
  name: '',
  tier: 'SUPPORTING',
  displayWeight: 1,
  socialLinks: [],
  productImageRefs: [],
});

export function SponsorFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const existing = useQuery({
    queryKey: ['sponsor', id],
    queryFn: () => catalogApi.sponsors.get(id!),
    enabled: Boolean(id),
  });
  const [form, setForm] = useState<UpsertSponsorRequest | null>(id ? null : blank());
  if (id && existing.data && !form) {
    const s = existing.data;
    setForm({
      name: s.name,
      tier: s.tier,
      displayWeight: s.displayWeight,
      logoLightRef: s.logoLightRef,
      logoDarkRef: s.logoDarkRef,
      brandColor: s.brandColor,
      tagline: s.tagline,
      websiteUrl: s.websiteUrl,
      socialLinks: s.socialLinks,
      ctaLabel: s.ctaLabel,
      ctaUrl: s.ctaUrl,
      productImageRefs: s.productImageRefs,
    });
  }
  const save = useMutation({
    mutationFn: (b: UpsertSponsorRequest) =>
      id ? catalogApi.sponsors.update(id, b) : catalogApi.sponsors.create(b),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ['sponsors'] });
      toast({ title: 'Sponsor saved', tone: 'success' });
      navigate(`/catalog/sponsors/${s.sponsorId}`);
    },
  });
  if (!form) return <p className="wd-muted">Loading…</p>;
  const set = <K extends keyof UpsertSponsorRequest>(k: K, v: UpsertSponsorRequest[K]) =>
    setForm({ ...form, [k]: v });
  const err = save.error && isApiError(save.error) ? save.error : null;
  return (
    <>
      <PageHeader
        title={id ? form.name || 'Sponsor' : 'New sponsor'}
        breadcrumb={[
          { label: 'Sponsors & prizes', to: '/catalog' },
          { label: 'Sponsors', to: '/catalog/sponsors' },
          { label: id ? 'Edit' : 'New' },
        ]}
      />
      <div className="wdc-form-grid">
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="sponsor-name" />
        </Field>
        <Field
          label="Tier"
          hint="Title: presented-by lockup everywhere. Gold: strip + carousel + prize credit. Supporting: footer strip."
        >
          <Select
            value={form.tier}
            onChange={(e) => set('tier', e.target.value as SponsorTier)}
            options={SponsorTier.options.map((t) => ({ value: t, label: t }))}
          />
        </Field>
        <Field label="Display weight" hint="Weighted rotation inside a tier (§7.1).">
          <Input
            type="number"
            min={1}
            max={100}
            value={form.displayWeight ?? 1}
            onChange={(e) => set('displayWeight', Math.min(100, Math.max(1, Number(e.target.value) || 1)))}
          />
        </Field>
        <Field label="Brand colour" hint="#RRGGBB; checked at 3:1 against the light canvas.">
          <Input
            value={form.brandColor ?? ''}
            onChange={(e) => set('brandColor', (e.target.value || undefined) as never)}
            placeholder="#1E3A8A"
          />
        </Field>
        <Field label="Tagline">
          <Input
            value={form.tagline?.en ?? ''}
            onChange={(e) => set('tagline', e.target.value ? { en: e.target.value } : undefined)}
          />
        </Field>
        <Field label="Website">
          <Input
            type="url"
            value={form.websiteUrl ?? ''}
            onChange={(e) => set('websiteUrl', (e.target.value || undefined) as never)}
          />
        </Field>
        <Field label="CTA label">
          <Input
            value={form.ctaLabel?.en ?? ''}
            onChange={(e) => set('ctaLabel', e.target.value ? { en: e.target.value } : undefined)}
          />
        </Field>
        <Field label="CTA URL">
          <Input
            type="url"
            value={form.ctaUrl ?? ''}
            onChange={(e) => set('ctaUrl', (e.target.value || undefined) as never)}
          />
        </Field>
        <Field label="Social links" hint="platform=url, one per line">
          <textarea
            className="wd-input"
            rows={3}
            value={(form.socialLinks ?? []).map((l) => `${l.platform}=${l.url}`).join('\n')}
            onChange={(e) =>
              set(
                'socialLinks',
                e.target.value
                  .split('\n')
                  .map((l) => l.split('='))
                  .filter((p) => p[0] && p[1])
                  .map(([platform, ...rest]) => ({ platform: platform!.trim(), url: rest.join('=').trim() })),
              )
            }
          />
        </Field>
      </div>
      <h2>Logos</h2>
      <div className="wdc-form-grid">
        <div>
          <AssetUploader
            purpose="SPONSOR_LOGO"
            label="Logo on light backgrounds"
            altSuggestion={`${form.name} logo`}
            onUploaded={(a) => set('logoLightRef', a.ref)}
          />
          {form.logoLightRef ? (
            <p className="wd-muted">
              <code>{form.logoLightRef}</code>
            </p>
          ) : null}
        </div>
        <div>
          <AssetUploader
            purpose="SPONSOR_LOGO"
            label="Logo on dark backgrounds"
            altSuggestion={`${form.name} logo (dark)`}
            onUploaded={(a) => set('logoDarkRef', a.ref)}
          />
          {form.logoDarkRef ? (
            <p className="wd-muted">
              <code>{form.logoDarkRef}</code>
            </p>
          ) : null}
        </div>
      </div>
      {err ? (
        <div className="wdc-callout" role="alert">
          {err.message}
        </div>
      ) : null}
      <div className="wd-btn-group">
        <Button onClick={() => save.mutate(form)} loading={save.isPending} disabled={!form.name.trim()}>
          Save sponsor
        </Button>
        <Button variant="quiet" href="/catalog/sponsors">
          Cancel
        </Button>
      </div>
    </>
  );
}
